import { useCallback, useEffect, useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notification } from 'antd';

import { NOTIFICATION_MESSAGES, ROUTES } from '@/shared/lib/constants';
import type { SelectOption } from '@/shared/types';
import type { TagEditHook } from '@/shared/types/editHooks';

import { convertToTagItems, filterEntityLabels } from '@/entities/search';
import { useAvailableTags } from '@/entities/tags/model';
import { convertTagsToTree } from '@/entities/tags/model/tagTree';

import type { GlossaryTermDetail } from '@/features/glossary/api';
import { updateGlossaryTermTags } from '@/features/glossary/api/updateGlossaryTermTags';

type UseGlossaryTermTagEditParams = {
  termId: string;
  termFqn: string;
};

export const useGlossaryTermTagEdit = ({ termId, termFqn }: UseGlossaryTermTagEditParams): TagEditHook => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTagFQNs, setSelectedTagFQNs] = useState<string[]>([]);

  const { data: availableTags = [], isLoading: isLoadingTags } = useAvailableTags();

  const queryKey = useMemo(() => ['glossaryTermDetail', termFqn] as const, [termFqn]);

  const termData = queryClient.getQueryData<GlossaryTermDetail>(queryKey);
  const currentTags = useMemo(
    () =>
      convertToTagItems(
        filterEntityLabels(termData?.tags, {
          includeOnlySource: 'Classification',
        })
      ),
    [termData?.tags]
  );
  const currentTagFQNs = useMemo(() => currentTags.map((tag) => tag.tagFQN), [currentTags]);

  useEffect(() => {
    setSelectedTagFQNs(currentTagFQNs);
  }, [currentTagFQNs]);

  const tagsOptions = useMemo<SelectOption<string>[]>(
    () =>
      availableTags.map((tag) => ({
        value: tag.value,
        label: tag.label,
        displayLabel: tag.label,
        searchLabel: `${tag.label} ${tag.classification} ${tag.description}`,
      })),
    [availableTags]
  );

  const tagTreeData = useMemo(() => convertTagsToTree(availableTags), [availableTags]);

  const tagTreeSelectValue = useMemo(() => {
    return selectedTagFQNs.map((tagFQN) => {
      const tag = currentTags.find((t) => t.tagFQN === tagFQN);
      return {
        value: tagFQN,
        label: tag ? tag.displayName || tag.name : tagFQN,
      };
    });
  }, [selectedTagFQNs, currentTags]);

  const tagBadgeItems = useMemo(
    () =>
      currentTags.map((tag) => ({
        tagFQN: tag.tagFQN,
        name: tag.name,
        displayName: tag.displayName || tag.name,
        description: tag.description,
        onClick: () => {
          navigate(`${ROUTES.TAGS_DETAIL}/${encodeURIComponent(tag.tagFQN)}`);
        },
      })),
    [currentTags, navigate]
  );

  const updateTagsMutation = useMutation({
    mutationFn: async (newTagFQNs: string[]) => {
      await updateGlossaryTermTags(termId, newTagFQNs, tagsOptions, currentTags);
    },
    onError: (error) => {
      queryClient.invalidateQueries({ queryKey });
      notification.error({
        message: NOTIFICATION_MESSAGES.TAG_UPDATE_ERROR,
        description: error instanceof Error ? error.message : NOTIFICATION_MESSAGES.TAG_UPDATE_ERROR_DESC,
        duration: 5,
        placement: 'bottomLeft',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      notification.success({
        message: NOTIFICATION_MESSAGES.TAG_UPDATE_SUCCESS,
        description: NOTIFICATION_MESSAGES.TAG_UPDATE_SUCCESS_DESC,
        placement: 'bottomLeft',
        duration: 3,
      });
    },
  });

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    const latestTermData = queryClient.getQueryData<GlossaryTermDetail>(queryKey);
    const latestTags = convertToTagItems(
      filterEntityLabels(latestTermData?.tags, {
        includeOnlySource: 'Classification',
      })
    );
    const latestTagFQNs = latestTags.map((tag) => tag.tagFQN);
    setSelectedTagFQNs(latestTagFQNs);
  }, [queryClient, queryKey]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    const latestTermData = queryClient.getQueryData<GlossaryTermDetail>(queryKey);
    const latestTags = convertToTagItems(
      filterEntityLabels(latestTermData?.tags, {
        includeOnlySource: 'Classification',
      })
    );
    const latestTagFQNs = latestTags.map((tag) => tag.tagFQN);
    setSelectedTagFQNs(latestTagFQNs);
  }, [queryClient, queryKey]);

  const handleSubmit = useCallback(
    async (newTagFQNs: string[]) => {
      updateTagsMutation.mutate(newTagFQNs, {
        onSuccess: () => {
          setIsOpen(false);
        },
      });
    },
    [updateTagsMutation]
  );

  return {
    isOpen,
    tagTreeData,
    tagTreeSelectValue,
    tagBadgeItems,
    isLoadingTags,
    isUpdating: updateTagsMutation.isPending,
    handleOpen,
    handleClose,
    handleSubmit,
  };
};
